import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { EventsService, Event } from './events.service';
import {
  formatDateShort,
  getTimezoneAbbreviation,
  getUserTimezone
} from '../shared/timezone.utils';
import { getOptimizedImageUrl } from '../shared/image.utils';
@Component({
  selector: 'app-event-registration-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  template: `
    <div class="registration-page">
      <img src="assets/images/Specialties-Section-Hexa-Spiral.png" alt="" class="page-bg-watermark">

      <!-- Loading -->
      <div class="state-panel loading-state" *ngIf="isLoadingEvent">
        <mat-spinner diameter="36"></mat-spinner>
        <p>Loading event details…</p>
      </div>

      <!-- Error -->
      <div class="state-panel error-state" *ngIf="!isLoadingEvent && !event && loadError">
        <mat-icon>error_outline</mat-icon>
        <h2>{{ loadError }}</h2>
        <button mat-flat-button color="primary" class="pill-btn" routerLink="/events">
          <mat-icon>arrow_back</mat-icon>
          <span>Back to Events</span>
        </button>
      </div>

      <!-- Content -->
      <div class="registration-content" *ngIf="!isLoadingEvent && event">

        <!-- Event summary -->
        <mat-card class="event-summary-card">
          <div class="event-banner" [class.no-image]="!event.image">
            <img *ngIf="event.image" [src]="getBannerImageUrl()" [alt]="event.title">
            <mat-icon *ngIf="!event.image">event</mat-icon>
          </div>

          <mat-card-content>
            <span class="event-type-chip" *ngIf="event.type">{{ event.type }}</span>
            <h1 class="event-title">{{ event.title }}</h1>

            <div class="event-meta-grid">
              <div class="meta-item">
                <mat-icon>calendar_today</mat-icon>
                <span>{{ formatDate(event.start) }}</span>
              </div>
              <div class="meta-item">
                <mat-icon>schedule</mat-icon>
                <span>{{ formatTime(event.start) }}</span>
              </div>
              <div class="meta-item" *ngIf="event.platform">
                <mat-icon>videocam</mat-icon>
                <span>{{ event.platform }}</span>
              </div>
              <div class="meta-item" *ngIf="event.location">
                <mat-icon>place</mat-icon>
                <span>{{ event.location }}</span>
              </div>
            </div>

            <p class="event-description" *ngIf="event.description">{{ event.description }}</p>
          </mat-card-content>
        </mat-card>

        <!-- Registration form -->
        <mat-card class="registration-form-card" *ngIf="!submitted && !isEventPast">
          <mat-card-content>
            <h2 class="form-title">Register for this event</h2>
            <p class="form-subtitle">Fill in your details below — it only takes a minute.</p>

            <form [formGroup]="form" (ngSubmit)="onSubmit()">
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>First Name</mat-label>
                  <input matInput formControlName="firstName">
                  <mat-error *ngIf="form.get('firstName')?.hasError('required')">First name is required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Last Name</mat-label>
                  <input matInput formControlName="lastName">
                  <mat-error *ngIf="form.get('lastName')?.hasError('required')">Last name is required</mat-error>
                </mat-form-field>
              </div>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email</mat-label>
                <input matInput type="email" formControlName="email">
                <mat-error *ngIf="form.get('email')?.hasError('required')">Email is required</mat-error>
                <mat-error *ngIf="form.get('email')?.hasError('email')">Enter a valid email</mat-error>
              </mat-form-field>

              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Phone Number (optional)</mat-label>
                  <input matInput formControlName="phoneNumber">
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Country (optional)</mat-label>
                  <input matInput formControlName="country" placeholder="e.g. NG">
                </mat-form-field>
              </div>
<mat-form-field appearance="outline" class="full-width">
  <mat-label>Organization</mat-label>
  <input matInput formControlName="organization" placeholder="e.g. Lagos General Hospital">
</mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
  <mat-label>Role</mat-label>
  <input matInput formControlName="role" placeholder="e.g. Doctor, Nurse, General Attendee">
</mat-form-field>



              <div class="error-banner" *ngIf="errorMessage">
                <mat-icon>error_outline</mat-icon>
                <span>{{ errorMessage }}</span>
              </div>

              <button
                mat-flat-button
                color="primary"
                type="submit"
                class="pill-btn submit-btn full-width"
                [disabled]="form.invalid || isSubmitting">
                <mat-spinner *ngIf="isSubmitting" diameter="20"></mat-spinner>
                <span *ngIf="!isSubmitting">Complete Registration</span>
              </button>
            </form>
          </mat-card-content>
        </mat-card>
<!-- Event has passed -->
<mat-card class="registration-form-card" *ngIf="!submitted && isEventPast">
  <mat-card-content>
    <div class="success-panel">
      <mat-icon class="success-icon" style="color:#dc2626;">event_busy</mat-icon>
      <h2>This event has already ended</h2>
      <p>Registration is closed because this event's scheduled time has passed.</p>
      <button mat-stroked-button class="pill-btn" routerLink="/events">
        <mat-icon>arrow_back</mat-icon>
        <span>Back to Events</span>
      </button>
    </div>
  </mat-card-content>
</mat-card>
        <!-- Success -->
        <mat-card class="success-card" *ngIf="submitted">
          <mat-card-content>
            <div class="success-panel">
              <mat-icon class="success-icon">check_circle</mat-icon>
              <h2>You're registered!</h2>
              <p>{{ successMessage }}</p>
              <a
                *ngIf="meetingUrl"
                [href]="meetingUrl"
                target="_blank"
                rel="noopener noreferrer"
                mat-flat-button
                color="primary"
                class="pill-btn">
                <mat-icon>launch</mat-icon>
                <span>Join Meeting</span>
              </a>
              <button mat-stroked-button class="pill-btn" routerLink="/events">
                <mat-icon>arrow_back</mat-icon>
                <span>Back to Events</span>
              </button>
            </div>
          </mat-card-content>
        </mat-card>

      </div>
    </div>
  `,
  styles: [`
    /* ---------------------------------------------------------------
       e-Likita palette — kept in sync with home.component.scss
    --------------------------------------------------------------- */
    :host {
      --color-primary: #007664;
      --color-bg-light: #E2FFF5;
      --color-bg-lighter: #F4FFFB;
      --color-text-dark: #042B20;
      --color-text-body: #3A4F39;
      --color-text-muted: #5D775B;
      --color-white: #FFFFFF;
      --color-accent-card: #FFFBF2;

      display: block;
      font-family: 'Inter', sans-serif;
      background-color: var(--color-bg-lighter);
      min-height: 100vh;
    }

    .registration-page {
      position: relative;   /* containing box for the watermark */
      overflow: hidden;     /* clip it so it doesn't bleed past this page */
      max-width: 720px;
      margin: 0 auto;
      padding: 48px 20px 80px;
    }

    .page-bg-watermark {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0.1;
      pointer-events: none;
      z-index: 0;
    }

    /* ---------------- Loading / Error states ---------------- */
    .state-panel {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      text-align: center;
      padding: 96px 20px;
      color: var(--color-text-body);

      mat-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
      }

      h2 {
        font-family: 'Urbanist', sans-serif;
        font-size: 20px;
        font-weight: 700;
        color: var(--color-text-dark);
        margin: 0;
      }

      p { margin: 0; font-size: 14px; }
    }

    .error-state mat-icon {
      color: #dc2626;
    }

    .registration-content {
      position: relative;
      z-index: 1;
    }

    /* ---------------- Shared card look ---------------- */
    mat-card {
      border-radius: 20px !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06) !important;
      margin-bottom: 24px;
      overflow: hidden;
    }

    /* ---------------- Event summary card ---------------- */
    .event-summary-card {
      .event-banner {
        width: 100%;
        max-height: 220px; 
        background-color: var(--color-bg-light);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        img {
      width: auto;          
      max-width: 100%;
      height: auto;
      max-height: 220px;
      object-fit: contain;    
    }

        &.no-image {
          background: linear-gradient(135deg, var(--color-primary) 0%, #005a4d 100%);

          mat-icon {
            font-size: 56px;
            width: 56px;
            height: 56px;
            color: var(--color-white);
          }
        }
      }

      mat-card-content {
        padding: 28px 28px 32px !important;
      }

      .event-type-chip {
        display: inline-block;
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 700;
        text-transform: capitalize;
        letter-spacing: 0.03em;
        color: var(--color-primary);
        background-color: var(--color-bg-light);
        border: 1px solid rgba(0, 118, 100, 0.25);
        padding: 4px 12px;
        border-radius: 999px;
        margin-bottom: 12px;
      }

      .event-title {
        font-family: 'Urbanist', sans-serif;
        font-size: clamp(22px, 3vw, 30px);
        font-weight: 700;
        color: var(--color-text-dark);
        margin: 0 0 16px;
        text-transform: capitalize;
      }

      .event-meta-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 20px;
        margin-bottom: 20px;
      }

      .meta-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13.5px;
        font-weight: 600;
        color: var(--color-text-body);

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: var(--color-primary);
        }
      }

      .event-description {
        font-size: 14.5px;
        line-height: 1.65;
        color: var(--color-text-muted);
        margin: 0;
        padding-top: 16px;
        border-top: 1px solid var(--color-bg-light);
      }
    }

    /* ---------------- Registration form card ---------------- */
    .registration-form-card {
      mat-card-content {
        padding: 28px !important;
      }

      .form-title {
        font-family: 'Urbanist', sans-serif;
        font-size: 20px;
        font-weight: 700;
        color: var(--color-text-dark);
        margin: 0 0 4px;
      }

      .form-subtitle {
        font-size: 13.5px;
        color: var(--color-text-muted);
        margin: 0 0 20px;
      }

      .form-row {
        display: flex;
        gap: 16px;

        .full-width { flex: 1 1 0; }
      }

      mat-form-field {
        --mdc-outlined-text-field-focus-outline-color: #007664;
        --mdc-outlined-text-field-caret-color: #007664;
      }

      .full-width { width: 100%; margin-bottom: 4px; }
    }

    .error-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #dc2626;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 10px;
      padding: 12px 14px;
      margin-bottom: 12px;
      font-size: 13.5px;
    }

    /* ---------------- Pill buttons (matches hero-primary-btn) ---------------- */
    .pill-btn {
      min-width: 200px;
      height: 48px;
      font-size: 14.5px;
      font-weight: 600;
      border-radius: 999px !important;
      transition: all 0.25s ease;

      mat-icon { margin-right: 8px; }
    }

    button.pill-btn[color="primary"],
    a.pill-btn[color="primary"] {
      background-color: var(--color-primary) !important;
      color: var(--color-white) !important;
      box-shadow: 0 4px 14px rgba(0, 118, 100, 0.3);

      &:hover:not([disabled]) {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 118, 100, 0.4);
      }
    }

    .submit-btn {
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    /* ---------------- Success card ---------------- */
    .success-card mat-card-content {
      padding: 40px 28px !important;
    }

    .success-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      text-align: center;

      .success-icon {
        font-size: 56px;
        width: 56px;
        height: 56px;
        color: var(--color-primary);
      }

      h2 {
        font-family: 'Urbanist', sans-serif;
        font-size: 22px;
        font-weight: 700;
        color: var(--color-text-dark);
        margin: 4px 0 0;
      }

      p {
        font-size: 14.5px;
        color: var(--color-text-body);
        margin: 0 0 12px;
        max-width: 420px;
      }

      .pill-btn { margin-top: 4px; }
    }

    /* ---------------- Mobile ---------------- */
    @media (max-width: 600px) {
      .registration-page { padding: 32px 16px 60px; }

      .registration-form-card .form-row {
        flex-direction: column;
        gap: 0;
      }

      .event-summary-card .event-banner {
    max-height: 160px; 

    img {
      max-height: 160px;
    }
      }}
  `]
})
export class EventRegistrationPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private eventsService = inject(EventsService);

  event: Event | null = null;
  isLoadingEvent = true;
  loadError = '';

  form: FormGroup = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phoneNumber: [''],
    role: [''],
    country: [''],
    organization: ['']
  });

  isSubmitting = false;
  submitted = false;
  errorMessage = '';
  successMessage = '';
  meetingUrl: string | undefined;
getBannerImageUrl(): string {
  return getOptimizedImageUrl(this.event?.image, 1920, 1080);
}
  get isEventPast(): boolean {
    if (!this.event) return false;
    return new Date(this.event.end + 'Z') < new Date();
  }
  ngOnInit(): void {
    const eventId = this.route.snapshot.paramMap.get('eventId');
    if (!eventId) {
      this.loadError = 'No event specified.';
      this.isLoadingEvent = false;
      return;
    }

    this.eventsService.getEventById(eventId).subscribe({
      next: (response) => {
        this.event = response.data;
        this.isLoadingEvent = false;
      },
      error: () => {
        this.loadError = 'Event not found.';
        this.isLoadingEvent = false;
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid || !this.event) return;

    this.isSubmitting = true;
    this.errorMessage = '';

    this.eventsService.registerForEvent(this.event.id, this.form.value).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.submitted = true;
        this.successMessage = response.data.message;
        this.meetingUrl = response.data.meetingUrl;
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err?.error?.message || 'Something went wrong. Please try again.';
      }
    });
  }

  formatDate(dateString: string): string {
    try {
      return formatDateShort(dateString);
    } catch {
      return 'Invalid date';
    }
  }

  formatTime(dateString: string): string {
    try {
      const date = new Date(dateString + 'Z');
      const timeFormat = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: getUserTimezone()
      });
      const time = timeFormat.format(date);
      const timezone = getTimezoneAbbreviation();
      return `${time} ${timezone}`;
    } catch {
      return 'Invalid time';
    }
  }
}